// Unless explicitly stated otherwise all files in this repository are licensed under the MIT License.
// This product includes software developed at Datadog (https://www.datadoghq.com/).
// Copyright 2019-Present Datadog, Inc.

import { INJECTED_FILE } from '@dd/core/constants';
import { outputFileSync, rmSync } from '@dd/core/helpers/fs';
import type { GlobalContext, Logger, PluginOptions, ToInjectItem } from '@dd/core/types';
import { InjectPosition } from '@dd/core/types';
import path from 'path';

import { PLUGIN_NAME } from './constants';
import {
    getContentToInject,
    prepareInjections,
    isFileSupported,
    warnUnsupportedFile,
} from './helpers';
import type { ContentsToInject } from './types';

export const getXpackPlugin =
    (
        bundler: any,
        log: Logger,
        context: GlobalContext,
        toInject: ToInjectItem[],
        contentsToInject: ContentsToInject,
    ): PluginOptions['rspack'] & PluginOptions['webpack'] =>
    (compiler) => {
        const cache = new WeakMap();
        // Use a narrower identifier to avoid cross build collisions.
        const ConcatSource = bundler.sources.ConcatSource;
        const id = context.bundler.name;
        // Use a cache directory within the project root instead of outDir,
        // so that webpack's output.clean option doesn't delete the file
        // before it can be resolved as an entry point.
        // https://github.com/DataDog/build-plugins/issues/304
        const filePath = path.resolve(
            context.buildRoot,
            'node_modules',
            '.cache',
            'datadog-build-plugins',
            `${id}.${InjectPosition.MIDDLE}.${INJECTED_FILE}.js`,
        );

        // NOTE: RSpack MAY try to resolve the entry points before the loader is ready.
        // There must be some race condition around this, because it's not always failing.
        outputFileSync(filePath, '');
        // WARNING: Can't use shutdown.tapPromise as rspack would randomly crash the process.
        // Seems to be fixed in rspack@1.2.*
        // We also do it for webpack, as it fixes some resolution edge cases.
        const hookFn = () => {
            // Delete the file we created.
            rmSync(filePath);
        };
        compiler.hooks.shutdown.tap(PLUGIN_NAME, hookFn);

        // Handle the InjectPosition.MIDDLE.
        type Entry = typeof compiler.options.entry;
        // TODO: Move this into @dd/core, add rspack/webpack types and tests.
        const injectEntry = (initialEntry: Entry): Entry => {
            const injectedEntry = {
                import: [filePath],
            };

            const objectInjection = (entry: Entry) => {
                for (const [entryKey, entryValue] of Object.entries(entry)) {
                    if (typeof entryValue === 'object') {
                        entryValue.import = entryValue.import || [];
                        entryValue.import.unshift(filePath);
                    } else if (typeof entryValue === 'string') {
                        // @ts-expect-error - Badly typed for strings.
                        entry[entryKey] = [filePath, entryValue];
                    } else if (Array.isArray(entryValue)) {
                        entryValue.unshift(filePath);
                    } else {
                        log.error(`Invalid entry type: ${typeof entryValue}`);
                    }
                }
            };

            if (!initialEntry) {
                return {
                    ddHelper: injectedEntry,
                };
            } else if (typeof initialEntry === 'function') {
                // @ts-expect-error - This is webpack / rspack typing conflict.
                return async () => {
                    const originEntry = await initialEntry();
                    objectInjection(originEntry);
                    return originEntry;
                };
            } else if (typeof initialEntry === 'object') {
                objectInjection(initialEntry);
            } else if (typeof initialEntry === 'string') {
                // @ts-expect-error - Badly typed for strings.
                return [injectedEntry, initialEntry];
            } else {
                log.error(`Invalid entry type: ${typeof initialEntry}`);
                return initialEntry;
            }
            return initialEntry;
        };

        // We need to prepare the injections before the build starts.
        // Otherwise they'll be empty once resolved.
        const setupInjections = async () => {
            // Prepare the injections.
            await prepareInjections(log, toInject, contentsToInject, context.buildRoot);
        };

        // For one-time builds (production mode)
        compiler.hooks.beforeRun.tapPromise(PLUGIN_NAME, setupInjections);

        // For watch mode / dev server (webpack dev mode)
        compiler.hooks.watchRun.tapPromise(PLUGIN_NAME, setupInjections);

        // Handle the InjectPosition.START and InjectPosition.END.
        // This is a re-implementation of the BannerPlugin,
        // that is compatible with all versions of webpack and rspack,
        // with both banner and footer.
        compiler.hooks.compilation.tap(PLUGIN_NAME, (compilation) => {
            const hookCb = () => {
                for (const chunk of compilation.chunks) {
                    const isEntry = chunk.canBeInitial();
                    // Per-chunk content (e.g. context snippets) keys off the chunk
                    // content hash, the only chunk source available at this stage.
                    const chunkSource = chunk.contentHash?.javascript ?? chunk.hash;

                    for (const file of chunk.files) {
                        const { base, ext } = path.parse(file);
                        const isOutputSupported = isFileSupported(ext);
                        if (!isOutputSupported) {
                            warnUnsupportedFile(log, ext, base);
                            continue;
                        }

                        const fileName = base;
                        // Resolve static and per-chunk content in one pass.
                        const banner = getContentToInject(contentsToInject, InjectPosition.BEFORE, {
                            sourceOrHash: chunkSource,
                            fileName,
                            isEntry,
                        });
                        const footer = getContentToInject(contentsToInject, InjectPosition.AFTER, {
                            sourceOrHash: chunkSource,
                            fileName,
                            isEntry,
                        });

                        if (banner === '' && footer === '') {
                            continue;
                        }

                        compilation.updateAsset(file, (old) => {
                            const cached = cache.get(old);

                            // If anything changed, we need to re-create the source.
                            if (!cached || cached.banner !== banner || cached.footer !== footer) {
                                const source = new ConcatSource(banner, '\n', old, '\n', footer);

                                // Cache the result.
                                cache.set(old, { source, banner, footer });
                                return source;
                            }

                            return cached.source;
                        });
                    }
                }
            };

            const stage = bundler.Compilation.PROCESS_ASSETS_STAGE_ADDITIONS;
            compilation.hooks.processAssets.tap({ name: PLUGIN_NAME, stage }, hookCb);
        });

        // We inject the new entry.
        const newEntry = injectEntry(compiler.options.entry);
        compiler.options.entry = newEntry;
    };
